/**
 * V1.5.8.27 — Admin-editable sidebar order.
 *
 * Shown inside WorkspaceSettings, admin-only. Persists to
 * /api/workspace/nav-order (workspace-scoped). AppLayout listens for the
 * `naukri:nav-order-changed` event and refetches, so the change is live
 * for every recruiter / hiring-manager / admin in the workspace the next
 * time they navigate.
 *
 * Uses ↑/↓ arrow buttons (no drag lib — keeps the bundle lean and works on
 * touch devices out of the box).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, ArrowsClockwise, ListDashes } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { TENANT_NAV_CATALOG } from "@/components/AppLayout";

export default function SidebarOrderEditor() {
    // Working copy of the tenant nav ordering. Seeded from the saved
    // order (if any) then any items not in the saved order are appended
    // in catalog order — this handles new nav items added after a save.
    const [items, setItems] = useState(() => [...TENANT_NAV_CATALOG]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        api.get("/workspace/nav-order")
            .then((r) => {
                const saved = r.data?.order || [];
                if (!saved.length) return;
                const byPath = new Map(TENANT_NAV_CATALOG.map((n) => [n.to, n]));
                const ordered = [];
                for (const p of saved) {
                    if (byPath.has(p)) {
                        ordered.push(byPath.get(p));
                        byPath.delete(p);
                    }
                }
                // Append unsaved (newly-added) nav items at the end so the
                // admin doesn't lose them silently.
                for (const remaining of byPath.values()) ordered.push(remaining);
                setItems(ordered);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const move = (idx, delta) => {
        setItems((cur) => {
            const next = [...cur];
            const j = idx + delta;
            if (j < 0 || j >= next.length) return cur;
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });
        setDirty(true);
    };

    const reset = () => {
        setItems([...TENANT_NAV_CATALOG]);
        setDirty(true);
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/workspace/nav-order", {
                order: items.map((n) => n.to),
            });
            toast.success("Sidebar order saved. Recruiters & HMs will see the new order.");
            setDirty(false);
            // Tell every open AppLayout instance to refetch.
            window.dispatchEvent(new Event("naukri:nav-order-changed"));
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't save. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            data-testid="setting-sidebar-order"
            className="bg-white border border-[#E5E5E5] p-4 sm:p-6 mb-5"
        >
            <div className="flex items-start gap-3">
                <ListDashes size={22} weight="bold" className="text-cobalt mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div>
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                            Sidebar menu order
                        </h2>
                        <p className="text-xs text-[#525252] mt-1 leading-relaxed">
                            Reorder the left navigation for your whole workspace.
                            Each role (recruiter, HM, admin) sees only the items they have access to — but in this order.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                            <button
                                type="button"
                                onClick={reset}
                                disabled={busy}
                                data-testid="sidebar-order-reset-btn"
                                className="inline-flex items-center gap-1 px-3 py-2 text-[10px] font-black uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink disabled:opacity-60"
                            >
                                <ArrowsClockwise size={11} weight="bold" /> Reset
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={busy || !dirty || loading}
                                data-testid="sidebar-order-save-btn"
                                className="inline-flex items-center gap-1 px-3 py-2 text-[10px] font-black uppercase tracking-wider bg-ink text-white hover:opacity-90 disabled:opacity-40"
                            >
                                {busy ? "Saving…" : "Save order"}
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="mt-4 text-xs text-[#A3A3A3]">Loading current order…</div>
                    ) : (
                        <ul
                            data-testid="sidebar-order-list"
                            className="mt-4 border border-[#E5E5E5] divide-y divide-[#E5E5E5]"
                        >
                            {items.map((it, idx) => (
                                <li
                                    key={it.to}
                                    data-testid={`sidebar-order-item-${it.to.replace(/\W+/g, "-")}`}
                                    className="flex items-center gap-2 px-2.5 py-2 bg-white"
                                >
                                    <span className="text-[10px] font-black text-[#A3A3A3] tabular-nums w-6 shrink-0">
                                        {String(idx + 1).padStart(2, "0")}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-ink truncate">
                                            {it.label}
                                        </div>
                                        <div className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3] truncate mt-0.5">
                                            {it.roles
                                                .filter((r) => ["admin", "recruiter", "hiring_manager"].includes(r))
                                                .map((r) => r === "hiring_manager" ? "HM" : r)
                                                .join(" · ")}
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => move(idx, -1)}
                                            disabled={busy || idx === 0}
                                            data-testid={`sidebar-order-up-${idx}`}
                                            aria-label={`Move ${it.label} up`}
                                            className="p-2 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink disabled:opacity-30"
                                        >
                                            <ArrowUp size={12} weight="bold" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => move(idx, +1)}
                                            disabled={busy || idx === items.length - 1}
                                            data-testid={`sidebar-order-down-${idx}`}
                                            aria-label={`Move ${it.label} down`}
                                            className="p-2 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink disabled:opacity-30"
                                        >
                                            <ArrowDown size={12} weight="bold" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}
